import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import NotificationsDropdown from './NotificationsDropdown';
import { Menu } from 'lucide-react';
import api from '../../services/api';

const Header = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [avatarUrl, setAvatarUrl] = useState(null);

  useEffect(() => {
    let activeBlobUrl = null;
    const fetchAvatar = async () => {
      const empId = user?._id || user?.id;
      if (!empId) return;

      try {
        const docsRes = await api.get(`/employees/${empId}/documents`);
        const photoItem = docsRes.data?.find(item => item.type?.code === 'PHOTO' && item.document);
        if (photoItem && photoItem.document?._id) {
          const res = await api.get(`/employees/${empId}/documents/${photoItem.document._id}/download`, {
            responseType: 'blob'
          });
          const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
          activeBlobUrl = blobUrl;
          setAvatarUrl(blobUrl);
        }
      } catch (err) {
        // Silently fall back to initials if photo is unavailable or user doesn't have document uploaded
      }
    };

    fetchAvatar();

    return () => {
      if (activeBlobUrl) {
        window.URL.revokeObjectURL(activeBlobUrl);
      }
    };
  }, [user?._id, user?.id]);

  // Basic title mapping based on route
  const getTitle = () => {
    const path = location.pathname;
    if (path.includes('employees')) return 'Employees Directory';
    if (path.includes('attendance')) return 'Attendance Management';
    if (path.includes('leaves')) return 'Leave Tracking';
    if (path.includes('settings')) return 'System Settings';
    if (path.includes('profile')) return 'My Profile';
    if (path.includes('payroll')) return 'Payroll Management';
    return 'Dashboard';
  };

  return (
    <header className="h-16 bg-white/80 backdrop-blur-md border-b border-gray-100 flex items-center justify-between px-6 sticky top-0 z-20 shadow-sm transition-all duration-300">
      <div className="flex items-center gap-4">
        <button 
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 -ml-2 rounded-lg text-gray-500 hover:bg-gray-800 hover:text-white transition-colors focus:outline-none"
        >
          <Menu size={24} />
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-gray-800 tracking-tight truncate max-w-[150px] md:max-w-none">{getTitle()}</h1>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <NotificationsDropdown />

        <div className="h-6 w-px bg-gray-200 mx-2"></div>

        {/* User Profile */}
        <div className="flex items-center gap-3">
          <div className="hidden md:flex flex-col items-end">
            <span className="text-sm font-semibold text-gray-700 leading-tight">
              {user?.firstName} {user?.lastName}
            </span>
            <span className="text-xs text-blue-600 font-medium">{user?.role}</span>
          </div>
          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-100 to-indigo-100 flex items-center justify-center text-blue-700 font-bold border-2 border-white shadow-sm ring-2 ring-gray-50 overflow-hidden shrink-0">
            {avatarUrl ? (
              <img 
                src={avatarUrl} 
                alt={`${user?.firstName} ${user?.lastName}`} 
                className="w-full h-full object-cover" 
              />
            ) : (
              <span>{user?.firstName?.[0]}{user?.lastName?.[0]}</span>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
